import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";

export type CashBox = {
  id: string; branchId: string; branchName: string; branchPrefix?: string;
  openingAmount: string; openedAt?: string; targetFloatAmount: string; balance: string; topUpNeeded: string;
  lastCount: null | { id: string; businessDate: string; countedAmount: string; expectedAmount: string; differenceAmount: string; countedAt: string };
  yesterdayCounted: boolean;
};
export type CashDocument = { kind: "receipt" | "job" | "maintenance_job" | "collection" | "float" | "taking" | "return"; id: string; reference: string; jobId?: string | null; paymentId?: string | null };
export type CashMovement = {
  postingSequence?: string | number;
  calendarBalanceAfter?: string;
  jobId?: string | null; maintenancePaymentId?: string | null;
  expenseRecordId?: string | null; expenseRecordLineNumber?: number | null; recordReference?: string | null;
  responsibleUserId?: string | null; responsibleName?: string | null; responsiblePinConfirmedAt?: string | null;
  linkedDocuments?: CashDocument[]; deviceTime?: string | null; originalMovementType?: string | null;
  attributionOnly?: boolean; paidFromBranchId?: string; paidFromBranchName?: string;
  forBranchId?: string; forBranchName?: string; attributionStatus?: string;
  id: string; reference: string; cashBoxId: string; branchId: string; branchName: string;
  movementType: string; direction: "in" | "out"; amount: string; occurredAt: string; businessDate: string;
  createdAt: string; createdByUserId: string; actorName?: string; createdByName?: string;
  notes: string | null; counterpartyName: string | null;
  countReason?: string | null; countNote?: string | null;
  expenseLineItemName?: string; expenseLineItemNameAr?: string; expenseLineItemCode?: string; expenseLineItemRef?: string;
  expenseCategoryName?: string; expenseLineItemId?: string; expenseCategoryId?: string;
  balanceAfter: string; balanceBefore?: string; photoIds: string[]; hasPhoto: boolean; status: string;
  toClassify?: boolean;
  personalFloatId?: string | null;
  collectionId?: string | null; collectionStatus?: string | null; collectionDifference?: string | null; collectionResolved?: boolean;
  supplierReturnId?: string | null; returnNumber?: string | null;
  supplierReceiptId?: string | null; supplierName?: string | null; receiptReference?: string | null; settlementRef?: string | null; isReversed?: boolean;
  supplierPaymentId?: string | null; parentMovementId?: string | null;
  advanceRepaymentAllocations?:{advanceId:string;reference:string;amount:string;eventId:string;cashMovementId:string}[];
  canFinance?: boolean; aboveUsualMax?: string | null; aboveUsualBasis?: "current" | null;
  history?: { action: string; actorName?: string; createdAt: string; note?: string }[];
};
export type CashMovementsPage = {
   counts?: { id?: string; countedAt?: string; businessDate: string; branchId: string; differenceAmount: string; actorName?: string; createdByName?: string; adjustmentMovementId?: string | null; settledAt?: string | null; resolution?: unknown; resolved?: boolean }[];
  movements: CashMovement[]; page: number; total: number; totalDays: number; totalPages: number;
  businessDaysPerPage: number; totals: { moneyIn: string; moneyOut: string; net: string };
};
export type CashOverview = {
  collections?: import("@/components/branch-cash/Collections").CollectionOverviewData;
  takings?: import("@/components/branch-cash/TakingVisibility").TakingVisibilityData;
  boxes: CashBox[];
  totals: { balance: string; float: string; topup: string; lastCountAmount: string };
  canFinance?: boolean;
  canViewCashBoxes?: boolean;
  needsAttention: { noPhoto: { count: number; amount: string } | number; toClassify?: { count: number; amount: string } };
  latestEntries: CashMovement[];
  snapshot: string;
};
export type CashMetric = "balance" | "float" | "topup" | "last_count" | "no_photo" | "to_classify";
export type CashDrilldown = {
  rows: (CashMovement & { kind: "movement" | "opening" | "box" | "count"; branchName: string; reference?: string; amount?: string; signedAmount?: string; countedWith?: string; countedAt?: string; expectedAmount?: string; differenceAmount?: string; reason?: string; note?: string })[];
  total: number; totalAmount: string | null; page: number; pageSize: number; totalPages: number; snapshot: string;
};
export type CashFilters = { sharedView?:string;from?: string; to?: string; person?: string; categoryId?: string; lineItemId?: string; min?: string; max?: string; status?: string; types?: string; direction?: "in" | "out"; photo?: "no" | "yes"; q?: string };
export const cashScopeKey = (workspace?: string, user?: { id?: string; roleId?: string | null; managedLocationIds?: string[] } | null, rolePermissions?: string[]) =>
  [workspace || "", user?.id || "", user?.roleId || "", [...(user?.managedLocationIds || [])].sort().join(","), [...(rolePermissions || [])].sort().join(",")];
const cashParams = (ids: string[], filters: CashFilters = {}, page = 1) => {
  const query = new URLSearchParams({ branches: ids.join(","), page: String(page) });
  Object.entries(filters).forEach(([key, value]) => { if (value) query.set(key, value); });
  return query.toString();
};
// Cash is identity- and branch-scoped. Do not use the persistent offline GET cache.
const cashGet = async <T,>(path: string): Promise<T> => (await apiRequest("GET", `/api${path}`)).json();
export function useBranchCashBoxes(enabled = true) {
  const { currentWorkspace, user, roles } = useAuth();
  return useQuery<CashBox[]>({
    queryKey: ["branch-cash", "boxes", ...cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id))],
    queryFn: () => cashGet("/cash/boxes"),
    enabled: enabled && !!currentWorkspace?.id && !!user,
  });
}
export function useBranchCashMovements(branchIds: string[], filters: CashFilters = {}, page = 1, enabled = true, groupTakings = false) {
  const { currentWorkspace, user, roles } = useAuth();
  const scope = cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id));
  const ids = [...branchIds].sort();
  return useQuery<CashMovementsPage>({
    queryKey: ["branch-cash", "movements", ...scope, ids.join(","), filters, page, groupTakings],
    queryFn: () => cashGet(`/cash/movements?${cashParams(ids, filters, page)}${groupTakings ? "&groupTakings=true" : ""}`),
    // An empty picker is never interpreted as unfiltered/all branches by the server.
    enabled: enabled && !!currentWorkspace?.id && ids.length > 0,
  });
}
export function useCashBookData<T>(endpoint: string, branchIds: string[], filters: CashFilters & Record<string, string | undefined> = {}) {
  const { currentWorkspace, user, roles } = useAuth();
  const ids = branchIds.slice().sort();
  return useQuery<T>({
    queryKey: ["branch-cash", endpoint, ...cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)), ids.join(","), filters],
    queryFn: () => cashGet(`/cash/${endpoint}?${cashParams(ids, filters)}`),
    enabled: !!currentWorkspace?.id && ids.length > 0,
  });
}
export function useBranchCashOverview(branchIds: string[], enabled = true) {
  const { currentWorkspace, user, roles } = useAuth();
  const ids = [...branchIds].sort();
  const scope = cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id));
  return useQuery<CashOverview>({
    queryKey: ["branch-cash", "overview", ...scope, ids.join(",")],
    queryFn: () => cashGet(`/cash/overview?branches=${encodeURIComponent(ids.join(","))}`),
    enabled: enabled && !!currentWorkspace?.id && ids.length > 0,
  });
}
export function useBranchCashDrilldown(branchIds: string[], metric: CashMetric, page: number, snapshot?: string, enabled = true) {
  const { currentWorkspace, user, roles } = useAuth();
  const ids = [...branchIds].sort();
  const scope = cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id));
  return useQuery<CashDrilldown>({
    queryKey: ["branch-cash", "drilldown", ...scope, ids.join(","), metric, page, snapshot],
    queryFn: () => {
      const params = new URLSearchParams({ branches: ids.join(","), metric, page: String(page) });
      if (snapshot) params.set("snapshot", snapshot);
      return cashGet(`/cash/drilldown?${params}`);
    },
    enabled: enabled && !!currentWorkspace?.id && ids.length > 0,
  });
}
export function useBranchCashRecord(id: string | null, branchIds: string[]) {
  const { currentWorkspace, user, roles } = useAuth();
  const scope = cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id));
  return useQuery<CashMovement>({
    queryKey: ["branch-cash", "record", ...scope, branchIds.slice().sort().join(","), id],
    queryFn: () => cashGet(`/cash/movements/${encodeURIComponent(id!)}`),
    enabled: !!id && !!currentWorkspace?.id && branchIds.length > 0,
  });
}
export type CashInType = "topup" | "till" | "refund" | "other_in" | "opening_balance";
export type CashInPayload = { cashBoxId: string; movementType: CashInType; amount: string; occurredAt: string; notes: string | null; counterpartyName: string | null; idempotencyKey: string; expenseLineItemId?: string | null; expenseCategoryId?: string | null };
export function useCreateBranchCashMovement() {
  const { currentWorkspace } = useAuth();
  const client = useQueryClient();
  return useMutation<CashMovement, Error, CashInPayload>({
    // apiRequest is live-only: cash writes must never enter the offline replay queue.
    mutationFn: async payload => (await apiRequest("POST", "/api/cash/movements", payload)).json(),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: ["branch-cash"] }),
      client.invalidateQueries({ queryKey: ["maintenance-evidence"] }),
      client.invalidateQueries({ queryKey: ["/api/maintenance/jobs/bootstrap"] }),
      client.invalidateQueries({ queryKey: ["/api/maintenance/parts/bootstrap"] }),
      client.invalidateQueries({ queryKey: ["/api/maintenance/brand-comparisons"] }),
    ]),
  });
}